'use client';

import dynamic from 'next/dynamic';

import type { HomeComponentMap } from '../../HomeSectionRenderer';
import CategoryNavBig from '../../components/CategoryNavBig/CategoryNavBig';
import CategoryNavSmall from '../../components/CategoryNavSmall/CategoryNavSmall';
import FivePicSwiper from '../../components/FivePicSwiper/FivePicSwiper';
import HeroBanner from '../../components/HomeBanner/HomeBanner';
import MultiSwipe from '../../components/MultiSwipe/MultiSwipe';
import OneLineTwo from '../../components/OneLineTwo/OneLineTwo';
import ProductList from '../../components/ProductList';
import type { BlogArticleProps } from './components/BlogArticle/types';
import Bestsellers2026 from './components/Bestsellers2026/Bestsellers2026';
import CategoryNavShape2026 from './components/CategoryNavShape2026/CategoryNavShape2026';
import MaterialGallery2026 from './components/MaterialGallery2026/MaterialGallery2026';
import NewArrivals2026 from './components/NewArrivals2026/NewArrivals2026';
import OneRowFourNav from './components/OneRowFourNav/OneRowFourNav';
import ProductInspirationShowcase2026 from './components/ProductInspirationShowcase2026/ProductInspirationShowcase2026';
import StyleGuideCards2026 from './components/StyleGuideCards2026/StyleGuideCards2026';

const BLOG_ARTICLE_SKELETON_ITEMS = ['blog-card-1', 'blog-card-2', 'blog-card-3', 'blog-card-4'];

function BlogArticleCardsLoading() {
  return (
    <div className="mt-[32px] grid grid-cols-4 gap-[12px] px-[48px]">
      {BLOG_ARTICLE_SKELETON_ITEMS.map((item) => (
        <div key={item} className="h-[371px] rounded-[12px] bg-white" />
      ))}
    </div>
  );
}

function BlogArticleTitleLoading() {
  return (
    <div className="h-[562px] w-full bg-[#f2f3f5] py-[60px]" aria-hidden>
      <div className="mx-auto h-[39px] w-[480px] max-w-[50%] rounded-[8px] bg-white/60" />
      <BlogArticleCardsLoading />
    </div>
  );
}

function BlogArticleTitleWithSubtitleLoading() {
  return (
    <div className="h-[594px] w-full bg-[#f2f3f5] py-[60px]" aria-hidden>
      <div className="flex flex-col items-center gap-[8px] px-[24px]">
        <div className="h-[39px] w-[480px] max-w-[50%] rounded-[8px] bg-white/60" />
        <div className="h-[24px] w-[720px] max-w-[75%] rounded-[8px] bg-white/50" />
      </div>
      <BlogArticleCardsLoading />
    </div>
  );
}

function BrandImageLoading() {
  return (
    <div
      className="flex h-[460px] w-full items-center bg-primary-purple px-[48px] xl:px-[72px]"
      aria-hidden
    >
      <div className="flex w-full items-start gap-[80px]">
        <div className="h-[47px] w-[350px] shrink-0 rounded-[8px] bg-white/20 xl:h-[67px] xl:w-[500px]" />
        <div className="flex min-w-0 flex-1 flex-col gap-[16px]">
          <div className="h-[48px] w-[60%] rounded-[8px] bg-white/20" />
          <div className="h-[192px] w-full rounded-[8px] bg-white/15" />
        </div>
      </div>
    </div>
  );
}

const BlogArticleTitle = dynamic<BlogArticleProps>(
  () => import('./components/BlogArticle/BlogArticle'),
  {
    ssr: false,
    loading: BlogArticleTitleLoading,
  },
);
const BlogArticleTitleWithSubtitle = dynamic<BlogArticleProps>(
  () => import('./components/BlogArticle/BlogArticle'),
  {
    ssr: false,
    loading: BlogArticleTitleWithSubtitleLoading,
  },
);

function BlogArticle(props: BlogArticleProps) {
  const Component = props.indexData.layout_subtitle
    ? BlogArticleTitleWithSubtitle
    : BlogArticleTitle;

  return <Component {...props} />;
}

const BrandImage = dynamic(() => import('./components/BrandImage/BrandImage'), {
  ssr: false,
  loading: BrandImageLoading,
});
const StyleHub = dynamic(() => import('../../components/StyleHub/StyleHub'), {
  ssr: false,
  loading: () => <div className="h-[300px] animate-pulse bg-[#f5f5f5]" />,
});
const WaterFall = dynamic(() => import('../../components/WaterFall/WaterFall'), {
  ssr: false,
  loading: () => <div className="h-[400px] animate-pulse bg-[#f5f5f5]" />,
});

// 2026 PC 首页组件逐项显式注册，避免新版组件缺失时静默回退到旧实现。
export const NEW_2026_PC_COMPONENT_MAP: HomeComponentMap = {
  swipeImage: HeroBanner,
  categoryNavSmall: CategoryNavSmall,
  categoryNavShape: CategoryNavShape2026,
  categoryNavBig: CategoryNavBig,
  styleHub: StyleHub,
  multiSwipe: MultiSwipe,
  oneLineTwo: OneLineTwo,
  multiSwipeSecond: FivePicSwiper,
  blogArticle: BlogArticle,
  brandImage: BrandImage,
  regularList: ProductList,
  regularListNewArrivals: NewArrivals2026,
  regularListBestseller: Bestsellers2026,
  waterFall: WaterFall,
  oneRowFourNav: OneRowFourNav,
  styleGuideCards2026: StyleGuideCards2026,
  materialGallery2026: MaterialGallery2026,
  productInspirationShowcase2026: ProductInspirationShowcase2026,
};
